import { Injectable, Optional, SkipSelf, Injector, Type, InjectionToken } from '@angular/core';
import { HttpInterceptor, HttpRequest, HttpHandler } from '@angular/common/http';

function interceptorFactory(next: HttpHandler, interceptor: HttpInterceptor): HttpHandler {
  return {
    handle(req: HttpRequest<any>) {
      return interceptor.intercept(req, next);
    },
  };
}

function isInterceptor(obj: any): obj is HttpInterceptor {
  // TODO: edge case of Class in obj that have static handle function
  return typeof obj.intercept === 'function';
}

export const DYNAMIC_HTTP_INTERCEPTORS = new InjectionToken<HttpInterceptor[]>('DYNAMIC_HTTP_INTERCEPTORS');

@Injectable()
export class SgDynamicHttpInterception implements HttpInterceptor {
  private get interceptors(): HttpInterceptor[] {
    return this.root.interceptors;
  }
  private get chain(): HttpHandler | null {
    return this.root.chain;
  }
  private set chain(value: HttpHandler | null) {
    this.root.chain = value;
  }

  private root: SgDynamicHttpInterception;

  constructor(
    private injector: Injector,
    @Optional() @SkipSelf() private parent: SgDynamicHttpInterception
  ) {
    if (parent) {
      this.root = parent.root;
    } else {
      this.root = this;
      Object.defineProperty(this, 'chain', { value: null, writable: true, enumerable: true });
      Object.defineProperty(this, 'interceptors', { value: [] });
    }
  }

  /**
   * Detects http interceptors from the DI.
   * This will reset the entire interceptor stack, any interceptors added manuallly will be removed.
   */
  autoDiscovery(): void {
    const interceptors = this.injector.get(DYNAMIC_HTTP_INTERCEPTORS, [], { optional: true, self: true });
    for (const i of interceptors) {
      this.addInterceptor(i);
    }
  }

  addInterceptor(handler: HttpInterceptor['intercept']): this;
  addInterceptor<T extends HttpInterceptor>(interceptorInstance: T): this;
  addInterceptor<T extends HttpInterceptor>(interceptorClass: Type<T>, injector?: Injector): this;
  addInterceptor<T extends HttpInterceptor>(
    interceptorClass: HttpInterceptor['intercept'] | T | Type<T>,
    injector?: Injector
  ): this {
    let interceptor: T;
    if (isInterceptor(interceptorClass)) {
      interceptor = interceptorClass;
    } else if (isInterceptor(interceptorClass.prototype || {})) {
      if (!injector) {
        injector = this.injector;
      }
      interceptor = injector.get(interceptorClass);
    } else {
      interceptor = { intercept: interceptorClass } as any;
    }

    this.interceptors.push(interceptor);
    this.chain = null;
    return this;
  }

  removeInterceptor(interceptorInstance: HttpInterceptor): boolean {
    const idx = this.interceptors.indexOf(interceptorInstance);
    if (idx > -1) {
      this.interceptors.splice(idx, 1);
      this.chain = null;
      return true;
    }
    return false;
  }

  intercept(req: HttpRequest<any>, next: HttpHandler) {
    if (!this.chain) {
      this.chain = this.interceptors.reduceRight(interceptorFactory, next);
    }
    return this.chain.handle(req);
  }
}
